import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  BadgeCheck, ArrowLeft, Sparkles, ArrowRight, ShieldCheck,
  Mail, Stethoscope, MapPin, Briefcase, MessageCircle, Share2, Copy, Check, Lock,
} from 'lucide-react';

const SHARE_URL = 'https://docsite.care/whats-new/credentialed-signup';
const SHARE_MESSAGE = (
  `New on DocSite — your signup now captures your NMC registration, years of practice, ` +
  `city and primary specialisation upfront. Your public profile looks credible from minute one, ` +
  `and your setup guide can be emailed to your inbox in one tap.\n\n` +
  `1-pager: ${SHARE_URL}`
);

export default function CredentialedSignupLaunch() {
  const [copied, setCopied] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(SHARE_MESSAGE)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent("DocSite — credentialed signup + private setup guide")}&body=${encodeURIComponent(SHARE_MESSAGE)}`;
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'DocSite — credentialed signup', text: SHARE_MESSAGE, url: SHARE_URL }); }
      catch { /* cancelled */ }
    } else { copyLink(); }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(SHARE_URL); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* clipboard denied */ }
  };

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>DocSite — Credentialed signup + a private setup space for doctors</title>
        <meta name="description" content="Your DocSite signup now captures your NMC / State Council registration, years of practice, city and primary specialisation in one screen — so your public profile looks ready from minute one. Setup guide can be emailed to you in one tap." />
        <meta name="robots" content="index, follow" />
        <meta name="keywords" content="credentialed doctor signup, NMC registration India, RMP verification, doctor onboarding India, DocSite signup, doctor portfolio India" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="DocSite — Credentialed signup + a private setup space for doctors" />
        <meta property="og:description" content="Capture NMC, years, city, specialisation in one screen. Email yourself the 2-page setup guide. Your setup space stays private to you." />
        <meta property="og:url" content={SHARE_URL} />
        <meta property="article:published_time" content="2026-02-12" />
        <link rel="canonical" href={SHARE_URL} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org', '@type': 'NewsArticle',
          headline: 'Credentialed signup + a private setup space for doctors',
          description: 'DocSite now captures NMC registration, years of practice, city and primary specialisation at signup. Setup guide can be emailed in one tap.',
          datePublished: '2026-02-12', dateModified: '2026-02-12',
          author: { '@type': 'Organization', name: 'DocSite', url: 'https://docsite.care' },
          publisher: { '@type': 'Organization', name: 'DocSite', url: 'https://docsite.care',
            logo: { '@type': 'ImageObject', url: 'https://docsite.care/logo512.png' } },
          mainEntityOfPage: SHARE_URL,
        })}</script>
      </Helmet>

      <div className="max-w-2xl mx-auto px-5 sm:px-6 py-8 sm:py-12">
        <Link to="/whats-new" data-testid="credsignup-launch-back"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft size={16} /> All updates
        </Link>

        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Hero */}
          <div className="bg-gradient-to-br from-sky-700 via-sky-600 to-emerald-500 text-white p-6 sm:p-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-xs font-bold uppercase tracking-widest mb-4">
              <Sparkles size={14} /> Just Launched · 12 Feb 2026
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Sign up with your credentials. Your setup space stays private to you.
            </h1>
            <p className="text-sky-50 mt-3 text-sm sm:text-base leading-relaxed">
              Your signup now captures your NMC / State Medical Council registration number, your years
              of practice, your city, and your primary specialisation — all in one screen. Your public
              profile looks credible the moment you log in. And the dashboard, your screen-by-screen
              setup guide, and your 2-page printable are now visible only after you sign in.
            </p>
          </div>

          {/* Pills */}
          <div className="p-6 sm:p-8 space-y-6">
            <div className="grid sm:grid-cols-3 gap-3">
              <Pill icon={<BadgeCheck size={18} className="text-sky-600" />} title="Credentialed from minute one" body="NMC + city + specialisation captured upfront — your profile reads professional from day one." testid="credsignup-pill-credentialed" />
              <Pill icon={<Lock size={18} className="text-emerald-600" />} title="Setup space stays private" body="Your dashboard and step-by-step guide open only after you sign in." testid="credsignup-pill-private" />
              <Pill icon={<Mail size={18} className="text-amber-600" />} title="Guide in your inbox" body="One tap and the 2-page setup reference is emailed to your registered email." testid="credsignup-pill-email" />
            </div>

            {/* What's new on the signup screen */}
            <div className="rounded-2xl border-2 border-sky-200 bg-sky-50 p-5">
              <p className="text-[10px] font-bold text-sky-700 uppercase tracking-widest mb-2">New on the signup screen</p>
              <ul className="space-y-2 text-sm text-slate-800">
                <li className="flex items-start gap-2">
                  <Briefcase size={16} className="text-sky-700 mt-0.5 shrink-0" />
                  <span><b>Years of practice</b> — paints your public profile with a credibility-coloured "stage" badge (more years = warmer trust palette).</span>
                </li>
                <li className="flex items-start gap-2">
                  <ShieldCheck size={16} className="text-sky-700 mt-0.5 shrink-0" />
                  <span><b>NMC / State Medical Council registration number</b> — mandatory. Wrong or inactive numbers are terminated. This is what separates a real doctor portfolio from a directory listing.</span>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin size={16} className="text-sky-700 mt-0.5 shrink-0" />
                  <span><b>City of practice</b> — drives the long-tail searches that bring patients to your page ("ENT in Andheri", "diabetologist in Pune").</span>
                </li>
                <li className="flex items-start gap-2">
                  <Stethoscope size={16} className="text-sky-700 mt-0.5 shrink-0" />
                  <span><b>Primary specialisation</b> — picks from 22 medical disciplines including AYUSH (BAMS / BHMS), dentistry and physiotherapy.</span>
                </li>
              </ul>
              <p className="text-xs text-sky-800 mt-3">All four fields together = under 30 extra seconds. Your dashboard greets you with most of Step 1 already done.</p>
            </div>

            {/* Real-life scenarios */}
            <div className="space-y-3">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">How this helps you in real life</p>
              <Scenario
                icon={<BadgeCheck size={18} className="text-sky-600" />}
                title="Patient lands on your profile, day one"
                body="They see your registration number, your years of practice and your specialisation — credible signals that you're a verified doctor, not just a directory entry. Trust forms before they read a single line."
                testid="credsignup-scen-day-one"
              />
              <Scenario
                icon={<Mail size={18} className="text-amber-600" />}
                title="Setup guide, in your inbox"
                body="Open the visual walkthrough on your phone, tap 'Email me the 2-page reference guide'. It lands in your inbox in under a minute — print it, pin it next to your laptop, and you've got every step at a glance."
                testid="credsignup-scen-email"
              />
              <Scenario
                icon={<Lock size={18} className="text-emerald-600" />}
                title="Your setup screens stay private"
                body="Other clinics or aggregators can't snoop on your dashboard or your annotated setup walkthrough. Both now require sign-in. You stay in control of who sees your work-in-progress."
                testid="credsignup-scen-private"
              />
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Link to="/get-started" data-testid="credsignup-launch-cta-getstarted"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-semibold text-sm shadow-sm">
                Start signup <ArrowRight size={16} />
              </Link>
              <Link to="/whats-new" data-testid="credsignup-launch-cta-allupdates"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-sm">
                See all updates
              </Link>
            </div>

            {/* Share */}
            <div className="pt-4 border-t border-slate-200">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3">Tell a colleague</p>
              <div className="flex flex-wrap gap-2">
                <a href={whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="credsignup-launch-share-whatsapp"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold border border-emerald-200">
                  <MessageCircle size={14} /> WhatsApp
                </a>
                <a href={mailHref} data-testid="credsignup-launch-share-email"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700 text-xs font-semibold border border-sky-200">
                  <Mail size={14} /> Email
                </a>
                <button onClick={nativeShare} data-testid="credsignup-launch-share-native"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-semibold border border-slate-200">
                  <Share2 size={14} /> Share
                </button>
                <button onClick={copyLink} data-testid="credsignup-launch-share-copy"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-semibold border border-slate-200">
                  {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy link</>}
                </button>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-6">
          <BadgeCheck size={12} className="inline mr-1" />Already registered? Sign in to your dashboard for the screen-by-screen setup guide.
        </p>
      </div>
    </div>
  );
}

function Pill({ icon, title, body, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">{icon}<p className="text-sm font-semibold text-slate-900">{title}</p></div>
      <p className="text-xs text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

function Scenario({ icon, title, body, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-start gap-3" data-testid={testid}>
      <div className="w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        <p className="text-xs text-slate-600 mt-1 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
